import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useStableAvatar } from "@/hooks/use-stable-avatar";
import { useProfileReadingData } from "@/hooks/use-profile-reading-data";

export function useReadingProgress() {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();
  
  // Use the unified profile reading data hook
  const { data: profileData, isLoading, error, refetch } = useProfileReadingData();

  // Mutation to update reading goal for current profile with optimistic updates
  const updateReadingGoalMutation = useMutation({
    mutationFn: async (newGoal: number) => {
      if (!userId || !currentProfile?.id) throw new Error('User not authenticated or profile not selected');
      
      // Update the current profile's settings by fetching fresh data first
      const { data: currentProfileData, error: fetchError } = await supabase
        .from('user_profiles')
        .select('profile_settings')
        .eq('id', currentProfile.id)
        .single();
      
      if (fetchError) {
        console.error('Fetch profile error:', fetchError);
        throw fetchError;
      }
      
      // Clean up: remove legacy reading_goal field and use only readingGoal
      const { reading_goal, ...cleanSettings } = (currentProfileData?.profile_settings as any || {});
      const updatedSettings = {
        ...cleanSettings,
        readingGoal: newGoal
      };

      const { error } = await supabase
        .from('user_profiles')
        .update({ profile_settings: updatedSettings })
        .eq('id', currentProfile.id);

      if (error) {
        console.error('Update error:', error);
        throw error;
      }

      return newGoal;
    },
    onMutate: async (newGoal) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['profileReadingData', userId, currentProfile?.id] });
      
      // Snapshot previous value
      const previousData = queryClient.getQueryData(['profileReadingData', userId, currentProfile?.id]);
      
      // Optimistically update to the new value
      queryClient.setQueryData(['profileReadingData', userId, currentProfile?.id], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          readingGoal: newGoal
        };
      });
      
      return { previousData };
    },
    onError: (error, newGoal, context) => {
      // Rollback to previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(['profileReadingData', userId, currentProfile?.id], context.previousData);
      }
      console.error('Failed to update reading goal:', error);
    },
    onSuccess: async () => {
      // Invalidate and immediately refetch to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['profileReadingData', userId, currentProfile?.id] });
      await queryClient.invalidateQueries({ queryKey: ['profileReadingData'] });
      queryClient.invalidateQueries({ queryKey: ['readingGoal'] });
      queryClient.invalidateQueries({ queryKey: ['booksCompleted'] });
      // Force refetch to get updated data
      refetch();
    }
  });

  return {
    currentStreak: profileData.currentStreak,
    totalBooksRead: profileData.uniqueBooksCompleted,
    readingGoal: profileData.readingGoal,
    booksThisMonth: profileData.booksThisMonth,
    isLoading,
    isError: !!error,
    refetchBooksRead: refetch,
    updateReadingGoal: updateReadingGoalMutation.mutate,
    isUpdatingGoal: updateReadingGoalMutation.isPending
  };
}
